// src/pages/en/WatermarkPage_en.js
import React, { useEffect, useRef, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../WatermarkPage.css";

function WatermarkPageEn() {
  const canvasRef = useRef(null);
  const imgObjRef = useRef(null);
  const objectUrlRef = useRef(null);

  // Hidden file input ref
  const fileInputRef = useRef(null);

  // Watermark text input ref
  const inputRef = useRef(null);

  const [darkMode, setDarkMode] = useState(false);

  // CSR canonical URL
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Watermark Generator",
      description:
        "Apply a text watermark to images as single or repeated in horizontal/vertical/diagonal and save as PNG with TooLoves Watermark Generator.",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "watermark, image watermark, text watermark, diagonal watermark, repeating watermark, PNG, TooLoves, free",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // Sync theme-color with dark/light mode
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "theme-color";
      newMeta.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(newMeta);
    }
  }, [darkMode]);

  // Image/watermark state
  const [imageLoaded, setImageLoaded] = useState(false);
  const [waterText, setWaterText] = useState("");
  const [fontSize, setFontSize] = useState(48);
  const [color, setColor] = useState("#111827");
  const [opacity, setOpacity] = useState(0.2); // 0.05~1
  const [orientation, setOrientation] = useState("horizontal"); // horizontal | vertical | diagonal
  const [repeatX, setRepeatX] = useState(false); // repeat horizontally
  const [repeatY, setRepeatY] = useState(false); // repeat vertically
  const [gap, setGap] = useState(120); // repeat gap (px)
  const [position, setPosition] = useState("center"); // single placement position

  // Trigger hidden file input
  const onPickClick = () => {
    if (fileInputRef.current) {
      try {
        fileInputRef.current.value = "";
      } catch {}
      fileInputRef.current.click();
    }
  };

  // Load image on file selection
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    const objUrl = URL.createObjectURL(file);
    objectUrlRef.current = objUrl;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgObjRef.current = img;
      setImageLoaded(true);
      drawCanvas();
      URL.revokeObjectURL(objUrl);
      objectUrlRef.current = null;
    };
    img.src = objUrl;
  };

  // Auto-resize textarea
  const autoResizeTextarea = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleTextKeyDown = (e) => {
    if (e.key === "Enter") {
      requestAnimationFrame(() => autoResizeTextarea());
    }
  };

  const handleTextPaste = () => {
    requestAnimationFrame(() => autoResizeTextarea());
  };

  useEffect(() => {
    autoResizeTextarea();
  }, [waterText]);

  useEffect(() => {
    autoResizeTextarea();
  }, []);

  // Canvas rendering
  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const img = imgObjRef.current;
    if (!canvas || !ctx || !img) return;

    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    if (!waterText.trim()) return;

    const fontFamily =
      "Pretendard, Segoe UI, Arial, Apple SD Gothic Neo, Noto Sans KR, sans-serif";
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const lines = waterText.replace(/\r\n?/g, "\n").split("\n");
    const lineHeight = Math.round(fontSize * 1.3);

    let maxLineW = 1;
    for (const line of lines) {
      const w = Math.max(1, ctx.measureText(line).width);
      if (w > maxLineW) maxLineW = w;
    }
    const blockW = maxLineW;
    const blockH = Math.max(1, lines.length * lineHeight);

    const angle =
      orientation === "vertical"
        ? -Math.PI / 2
        : orientation === "diagonal"
        ? -Math.PI / 4
        : 0;

    const cos = Math.cos(Math.abs(angle));
    const sin = Math.sin(Math.abs(angle));
    const rotW = angle !== 0 ? blockW * cos + blockH * sin : blockW;
    const rotH = angle !== 0 ? blockW * sin + blockH * cos : blockH;

    const drawAt = (x, y) => {
      ctx.save();
      ctx.globalAlpha = Math.max(0.01, Math.min(1, opacity));
      ctx.fillStyle = color;
      const mid = (lines.length - 1) / 2;
      if (angle !== 0) {
        ctx.translate(x, y);
        ctx.rotate(angle);
        lines.forEach((line, i) => {
          const yOff = (i - mid) * lineHeight;
          ctx.fillText(line, 0, yOff);
        });
      } else {
        lines.forEach((line, i) => {
          const yOff = (i - mid) * lineHeight;
          ctx.fillText(line, x, y + yOff);
        });
      }
      ctx.restore();
    };

    const stepX = rotW + gap;
    const stepY = rotH + gap;
    if (repeatX || repeatY) {
      const xs =
        repeatX && stepX > 0
          ? Array.from(
              { length: Math.ceil(canvas.width / stepX) + 2 },
              (_, i) => (i - 1) * stepX + stepX / 2
            )
          : [canvas.width / 2];
      const ys =
        repeatY && stepY > 0
          ? Array.from(
              { length: Math.ceil(canvas.height / stepY) + 2 },
              (_, i) => (i - 1) * stepY + stepY / 2
            )
          : [canvas.height / 2];

      for (const y of ys) {
        for (const x of xs) {
          drawAt(x, y);
        }
      }
      return;
    }

    let x = canvas.width / 2;
    let y = canvas.height / 2;
    const pad = 24;
    if (position === "top-left") {
      x = pad + rotW / 2;
      y = pad + rotH / 2;
    } else if (position === "top-right") {
      x = canvas.width - pad - rotW / 2;
      y = pad + rotH / 2;
    } else if (position === "bottom-left") {
      x = pad + rotW / 2;
      y = canvas.height - pad - rotH / 2;
    } else if (position === "bottom-right") {
      x = canvas.width - pad - rotW / 2;
      y = canvas.height - pad - rotH / 2;
    }
    drawAt(x, y);
  };

  useEffect(() => {
    drawCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    imageLoaded,
    waterText,
    fontSize,
    color,
    opacity,
    orientation,
    repeatX,
    repeatY,
    gap,
    position,
  ]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = "watermark.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleReset = () => {
    setWaterText("");
    setFontSize(48);
    setColor("#111827");
    setOpacity(0.2);
    setOrientation("horizontal");
    setRepeatX(false);
    setRepeatY(false);
    setGap(120);
    setPosition("center");
    setImageLoaded(false);
    imgObjRef.current = null;

    requestAnimationFrame(() => autoResizeTextarea());
  };

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "en" }}>
        <title>Watermark Generator | TooLoves</title>
        <meta
          name="description"
          content="Apply a text watermark to images as single or repeated in horizontal/vertical/diagonal and save as PNG."
        />
        <meta
          name="keywords"
          content="watermark, image watermark, text watermark, diagonal watermark, repeating watermark, PNG, TooLoves"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="Watermark Generator | TooLoves" />
        <meta
          property="og:description"
          content="Add a watermark to images and save as PNG."
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-watermark.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Watermark Generator | TooLoves" />
        <meta
          name="twitter:description"
          content="Add a watermark to images and save as PNG."
        />
        <meta name="twitter:image" content="/og-watermark.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* App bar */}
      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/en" className="brand-link" aria-label="Go to TooLoves home">
            TooLoves
          </Link>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="mode-toggle" onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? "Light mode" : "Dark mode"}
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="container">
        <section className="generator-card">
          <h2 className="card-title">Image Watermark</h2>
          <p className="card-subtitle">
            Upload an image, enter text, apply a semi-transparent watermark as single
            or repeated in horizontal/vertical/diagonal, and save as PNG.
          </p>

          <form className="form" onSubmit={(e) => e.preventDefault()}>
            {/* Image upload (hidden input + button) */}
            <div className="input-row">
              <input
                ref={fileInputRef}
                id="wm-file-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={onPickClick}
                aria-label="Choose image file"
                className="btn btn-primary"
              >
                Choose file
              </button>
            </div>

            {/* Watermark text */}
            <div className="input-row" style={{ marginTop: 8 }}>
              <textarea
                ref={inputRef}
                className="text-input text-inputtextarea"
                placeholder="Enter watermark text"
                value={waterText}
                onChange={(e) => {
                  setWaterText(e.target.value);
                  autoResizeTextarea();
                }}
                onKeyDown={handleTextKeyDown}
                onPaste={handleTextPaste}
                rows={1}
              />
            </div>

            {/* Options */}
            <div className="options-row">
              <div className="field">
                <label className="form-label">Font size</label>
                <input
                  type="range"
                  min="16"
                  max="160"
                  step="2"
                  value={fontSize}
                  onChange={(e) => setFontSize(Number(e.target.value))}
                />
                <div className="hint">{fontSize}px</div>
              </div>

              <div className="field">
                <label className="form-label">Text color</label>
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
              </div>

              <div className="field">
                <label className="form-label">Opacity</label>
                <input
                  type="range"
                  min="0.05"
                  max="1"
                  step="0.05"
                  value={opacity}
                  onChange={(e) => setOpacity(Number(e.target.value))}
                />
                <div className="hint">{Math.round(opacity * 100)}%</div>
              </div>

              <div className="field">
                <label className="form-label">Text direction</label>
                <select value={orientation} onChange={(e) => setOrientation(e.target.value)}>
                  <option value="horizontal">Horizontal</option>
                  <option value="vertical">Vertical</option>
                  <option value="diagonal">Diagonal</option>
                </select>
              </div>

              <div className="field switch">
                <label className="form-label">Repeat horizontally</label>
                <input
                  type="checkbox"
                  checked={repeatX}
                  onChange={(e) => setRepeatX(e.target.checked)}
                />
              </div>

              <div className="field switch">
                <label className="form-label">Repeat vertically</label>
                <input
                  type="checkbox"
                  checked={repeatY}
                  onChange={(e) => setRepeatY(e.target.checked)}
                />
              </div>

              <div className="field">
                <label className="form-label">Repeat gap</label>
                <input
                  type="range"
                  min="16"
                  max="400"
                  step="4"
                  value={gap}
                  onChange={(e) => setGap(Number(e.target.value))}
                  disabled={!(repeatX || repeatY)}
                />
                <div className="hint">{gap}px</div>
              </div>

              <div className="field">
                <label className="form-label">Single placement position</label>
                <select
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  disabled={repeatX || repeatY}
                >
                  <option value="center">Center</option>
                  <option value="top-left">Top-left</option>
                  <option value="top-right">Top-right</option>
                  <option value="bottom-left">Bottom-left</option>
                  <option value="bottom-right">Bottom-right</option>
                </select>
              </div>
            </div>
          </form>

          {/* Preview */}
          <div className="preview-card">
            {imageLoaded ? (
              <>
                <div className="canvas-wrap">
                  <canvas ref={canvasRef} id="wm-canvas" />
                </div>
                <div className="actions">
                  <button onClick={handleDownload}>Download PNG</button>
                  <button className="btn-secondary" onClick={handleReset}>
                    Reset
                  </button>
                </div>
              </>
            ) : (
              <p className="placeholder">Select an image and enter watermark text.</p>
            )}
          </div>
        </section>
      </main>

      <footer className="main-footer">
              <span>© 2025 TooLoves. All rights reserved.</span>
              <nav className="footer-links" aria-label="Legal links">
                <Link to="/en/terms" className="footer-link">
                  Terms of Service
                </Link>
                <span className="footer-sep" aria-hidden="true">
                  ·
                </span>
                <Link to="/en/privacy" className="footer-link">
                  Privacy Policy
                </Link>
              </nav>
            </footer>
          </div>
  );
}

export default WatermarkPageEn;